import { useEffect, useRef, useState } from "react";
import { Search, Plus, X, Check, User, Car, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Two-mode picker: search existing customer OR create-inline.
 *
 * Emits an object matching customer_snapshot shape.
 */
export function CustomerPicker({ value, onChange, disabled = false }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dupWarn, setDupWarn] = useState(null);
  const inputRef = useRef();
  const containerRef = useRef();

  // click outside to close
  useEffect(() => {
    const h = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // search on q change
  useEffect(() => {
    if (!q) return;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const r = await api.get("/customers", { params: { q } });
        setItems(r.data || []);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  const pick = (c) => {
    onChange({
      id: c.id,
      name: c.name,
      phone: c.phone,
      email: c.email || "",
      address: c.address || "",
      gstin: c.gstin || "",
      whatsapp: c.whatsapp || c.phone,
    });
    setOpen(false);
    setQ("");
  };

  const hasCust = !!(value?.name || value?.id);

  return (
    <div ref={containerRef} className="relative">
      {hasCust && !open ? (
        <div
          className="rounded-lg border border-[#E4E7EC] bg-white px-3 py-2.5 flex items-center gap-3"
          data-testid="est-customer-picked"
        >
          <div className="w-8 h-8 rounded-md bg-[#a8dadc]/40 text-[#457b9d] grid place-items-center shrink-0">
            <User size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] font-semibold text-[#1d3557] truncate">
              {value.name}
            </div>
            <div className="text-[11.5px] text-[#667085] truncate">
              {value.phone}{value.email ? ` · ${value.email}` : ""}
            </div>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
              className="text-[11.5px] font-semibold text-[#457b9d] hover:text-[#e63946]"
              data-testid="est-customer-change"
            >
              Change
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              disabled={disabled}
              placeholder="Search by name or phone…"
              data-testid="est-customer-search"
              className="w-full h-11 pl-9 pr-3 rounded-lg border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
            />
          </div>
          {open && (
            <div className="absolute z-20 top-[46px] left-0 right-0 bg-white border border-[#E4E7EC] rounded-lg shadow-lg max-h-72 overflow-auto">
              {loading ? (
                <div className="p-4 text-center text-[12px] text-[#667085]">Searching…</div>
              ) : q.length > 0 && items.length === 0 ? (
                <div className="p-3">
                  <div className="text-[12px] text-[#667085] mb-2">
                    No matches for &quot;<b>{q}</b>&quot;
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowNew(true); setOpen(false); }}
                    data-testid="est-customer-create"
                    className="w-full h-9 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center justify-center gap-1"
                  >
                    <Plus size={12} /> Create &quot;{q}&quot;
                  </button>
                </div>
              ) : items.length > 0 ? (
                <ul>
                  {items.slice(0, 20).map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => pick(c)}
                        data-testid={`est-customer-opt-${c.id}`}
                        className="w-full text-left p-2.5 hover:bg-[#F1FAEE] flex items-center gap-2.5"
                      >
                        <div className="w-8 h-8 rounded-md bg-[#a8dadc]/40 text-[#457b9d] grid place-items-center shrink-0">
                          <User size={13} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#1d3557] truncate">
                            {c.name}
                          </div>
                          <div className="text-[11px] text-[#667085] truncate">
                            {c.phone}{c.email ? ` · ${c.email}` : ""}
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                  <li className="border-t border-[#F1FAEE]">
                    <button
                      type="button"
                      onClick={() => { setShowNew(true); setOpen(false); }}
                      className="w-full text-left p-2.5 hover:bg-[#F7F9FC] text-[12px] font-semibold text-[#e63946]"
                    >
                      + Create a new customer
                    </button>
                  </li>
                </ul>
              ) : (
                <div className="p-3 text-[12px] text-[#667085]">Start typing to search…</div>
              )}
            </div>
          )}
        </>
      )}

      {showNew && (
        <NewCustomerInline
          seedName={q}
          onCancel={() => setShowNew(false)}
          onCreated={(c) => { pick(c); setShowNew(false); setDupWarn(null); }}
          onDuplicate={setDupWarn}
        />
      )}

      {dupWarn && (
        <div className="mt-2 rounded-lg border border-[#F79009]/30 bg-[#F79009]/10 px-3 py-2 text-[12px] text-[#7c4a02]">
          Existing customer found with this phone.
          <button
            type="button"
            onClick={() => pick(dupWarn)}
            className="ml-2 underline font-semibold"
          >
            Use {dupWarn.name}
          </button>
        </div>
      )}
    </div>
  );
}

function NewCustomerInline({ seedName, onCancel, onCreated, onDuplicate }) {
  const [f, setF] = useState({
    name: seedName || "", phone: "", email: "", address: "", gstin: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.name.trim() || !f.phone.trim()) return;
    setSaving(true);
    try {
      // Duplicate detection first
      const existing = await api.get("/customers", { params: { q: f.phone } });
      const dup = (existing.data || []).find((c) => c.phone === f.phone);
      if (dup) {
        onDuplicate?.(dup);
        setSaving(false);
        return;
      }
      const r = await api.post("/customers", {
        name: f.name.trim(),
        phone: f.phone.trim(),
        email: f.email.trim(),
        address: f.address.trim(),
      });
      // gstin isn't in backend model — patch to snapshot
      onCreated({ ...r.data, gstin: f.gstin });
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-3 rounded-lg border border-[#a8dadc]/60 bg-[#F1FAEE] p-3" data-testid="est-customer-newform">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12.5px] font-semibold text-[#1d3557]">New customer</div>
        <button onClick={onCancel} type="button" className="text-[#a8dadc] hover:text-[#e63946]">
          <X size={14} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <TxtField label="Name *" value={f.name} onChange={(v) => setF({ ...f, name: v })} testId="nc-name" />
        <TxtField label="Phone *" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testId="nc-phone" />
        <TxtField label="Email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testId="nc-email" />
        <TxtField label="GSTIN" value={f.gstin} onChange={(v) => setF({ ...f, gstin: v })} testId="nc-gstin" />
        <div className="md:col-span-2">
          <TxtField label="Address" value={f.address} onChange={(v) => setF({ ...f, address: v })} testId="nc-address" />
        </div>
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={saving || !f.name.trim() || !f.phone.trim()}
        data-testid="est-customer-save"
        className="mt-3 h-9 px-4 rounded-md bg-[#1d3557] hover:bg-[#152941] text-white text-[12.5px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
      >
        <Check size={12} /> {saving ? "Saving…" : "Save & Use"}
      </button>
    </div>
  );
}

const TxtField = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="mt-1 w-full h-9 px-2 rounded-md border border-[#E4E7EC] bg-white text-[13px] focus:border-[#457b9d] outline-none"
    />
  </label>
);


// =========================================================================
// Vehicle picker
// =========================================================================
export function VehiclePicker({ value, customerId, onChange, disabled = false }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const inputRef = useRef();
  const containerRef = useRef();

  useEffect(() => {
    const h = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    // preload vehicles for the picked customer
    if (customerId) {
      api.get("/vehicles", { params: { customer_id: customerId } }).then((r) => setItems(r.data || []));
    }
  }, [customerId]);

  const pick = (v) => {
    onChange({
      id: v.id,
      reg_number: v.reg_number,
      make: v.make, model: v.model, variant: v.variant || "",
      year: v.year, vin: v.vin || "", odometer: v.odometer || 0,
      fuel_type: v.fuel_type || "", color: v.color || "",
      service_type: v.service_type || "", transmission: v.transmission || "",
    });
    setOpen(false);
    setQ("");
  };

  const hasVeh = !!(value?.reg_number || value?.id);

  return (
    <div ref={containerRef} className="relative">
      {hasVeh && !open ? (
        <div
          className="rounded-lg border border-[#E4E7EC] bg-white px-3 py-2.5 flex items-center gap-3"
          data-testid="est-vehicle-picked"
        >
          <div className="w-8 h-8 rounded-md bg-[#a8dadc]/40 text-[#457b9d] grid place-items-center shrink-0">
            <Car size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] font-semibold text-[#1d3557] truncate">
              {value.reg_number || "—"} · {[value.make, value.model].filter(Boolean).join(" ")}
            </div>
            <div className="text-[11.5px] text-[#667085] truncate">
              {[value.fuel_type, value.year && `${value.year}`].filter(Boolean).join(" · ")}
            </div>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
              className="text-[11.5px] font-semibold text-[#457b9d] hover:text-[#e63946]"
              data-testid="est-vehicle-change"
            >
              Change
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setOpen(true)}
              disabled={disabled}
              placeholder={customerId ? "Search this customer's vehicles or type a new reg…" : "Type registration number…"}
              data-testid="est-vehicle-search"
              className="w-full h-11 pl-9 pr-3 rounded-lg border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
            />
          </div>
          {open && (
            <div className="absolute z-20 top-[46px] left-0 right-0 bg-white border border-[#E4E7EC] rounded-lg shadow-lg max-h-72 overflow-auto">
              {items.length > 0 ? (
                <ul>
                  {items
                    .filter((v) => !q || (v.reg_number || "").toLowerCase().includes(q.toLowerCase()))
                    .slice(0, 20)
                    .map((v) => (
                      <li key={v.id}>
                        <button
                          type="button"
                          onClick={() => pick(v)}
                          data-testid={`est-vehicle-opt-${v.id}`}
                          className="w-full text-left p-2.5 hover:bg-[#F1FAEE] flex items-center gap-2.5"
                        >
                          <div className="w-8 h-8 rounded-md bg-[#a8dadc]/40 text-[#457b9d] grid place-items-center shrink-0">
                            <Car size={13} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-[#1d3557] truncate font-mono">
                              {v.reg_number}
                            </div>
                            <div className="text-[11px] text-[#667085] truncate">
                              {[v.make, v.model].filter(Boolean).join(" ")}
                            </div>
                          </div>
                        </button>
                      </li>
                    ))}
                </ul>
              ) : (
                <div className="p-3 text-[12px] text-[#667085]">
                  {customerId ? "No vehicles saved for this customer." : "Pick a customer first."}
                </div>
              )}
              <div className="border-t border-[#F1FAEE]">
                <button
                  type="button"
                  onClick={() => { setShowNew(true); setOpen(false); }}
                  disabled={!customerId}
                  className="w-full text-left p-2.5 hover:bg-[#F7F9FC] text-[12px] font-semibold text-[#e63946] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="est-vehicle-create"
                >
                  + Add a new vehicle
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showNew && (
        <NewVehicleInline
          seedReg={q}
          customerId={customerId}
          onCancel={() => setShowNew(false)}
          onCreated={(v) => { pick(v); setShowNew(false); }}
        />
      )}
    </div>
  );
}

function NewVehicleInline({ seedReg, customerId, onCancel, onCreated }) {
  const [f, setF] = useState({
    reg_number: (seedReg || "").toUpperCase(), make: "", model: "", year: "", vin: "",
    fuel_type: "Petrol", odometer: "", color: "",
  });
  const [makes, setMakes] = useState({});
  const [customMake, setCustomMake] = useState(false);
  const [customModel, setCustomModel] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/master/vehicle-makes-v2").then((r) => setMakes(r.data || {})).catch(() => setMakes({}));
  }, []);
  const submit = async () => {
    if (!f.reg_number.trim() || !f.make || !f.model) return;
    setSaving(true);
    try {
      const r = await api.post("/vehicles", {
        customer_id: customerId,
        reg_number: f.reg_number.trim().toUpperCase(),
        make: f.make, model: f.model,
        year: f.year ? Number(f.year) : null,
        vin: f.vin, fuel_type: f.fuel_type,
        odometer: f.odometer ? Number(f.odometer) : 0,
        color: f.color,
      });
      onCreated(r.data);
    } finally { setSaving(false); }
  };
  const models = makes[f.make] || [];
  return (
    <div className="mt-3 rounded-lg border border-[#a8dadc]/60 bg-[#F1FAEE] p-3" data-testid="est-vehicle-newform">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12.5px] font-semibold text-[#1d3557]">New vehicle</div>
        <button onClick={onCancel} type="button" className="text-[#a8dadc] hover:text-[#e63946]">
          <X size={14} />
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <TxtField label="Reg No. *" value={f.reg_number} onChange={(v) => setF({ ...f, reg_number: v.toUpperCase() })} testId="nv-reg" />
        <div>
          {customMake ? (
            <TxtField label="Make * (custom)" value={f.make} onChange={(v) => setF({ ...f, make: v })} testId="nv-make-custom" />
          ) : (
            <SelectField label="Make *" value={f.make} onChange={(v) => setF({ ...f, make: v, model: "" })} options={Object.keys(makes)} testId="nv-make" />
          )}
          <button type="button" onClick={() => { setCustomMake(!customMake); setF({ ...f, make: "", model: "" }); setCustomModel(false); }}
                  data-testid="nv-make-toggle"
                  className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#e63946] hover:underline">
            {customMake ? "Pick from list" : "Not listed? Type in"}
          </button>
        </div>
        <div>
          {(customMake || customModel) ? (
            <TxtField label="Model * (custom)" value={f.model} onChange={(v) => setF({ ...f, model: v })} testId="nv-model-custom" />
          ) : (
            <SelectField label="Model *" value={f.model} onChange={(v) => setF({ ...f, model: v })} options={models} testId="nv-model" />
          )}
          {!customMake && (
            <button type="button" onClick={() => { setCustomModel(!customModel); setF({ ...f, model: "" }); }}
                    data-testid="nv-model-toggle"
                    className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#e63946] hover:underline">
              {customModel ? "Pick from list" : "Not listed? Type in"}
            </button>
          )}
        </div>
        <SelectField label="Fuel" value={f.fuel_type} onChange={(v) => setF({ ...f, fuel_type: v })} options={["Petrol", "Diesel", "CNG", "Petrol+CNG", "Electric", "Hybrid", "LPG"]} testId="nv-fuel" />
        <TxtField label="Year" value={f.year} onChange={(v) => setF({ ...f, year: v })} testId="nv-year" />
        <TxtField label="VIN" value={f.vin} onChange={(v) => setF({ ...f, vin: v })} testId="nv-vin" />
        <TxtField label="Odometer" value={f.odometer} onChange={(v) => setF({ ...f, odometer: v })} testId="nv-odo" />
        <TxtField label="Colour" value={f.color} onChange={(v) => setF({ ...f, color: v })} testId="nv-color" />
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={saving || !f.reg_number.trim() || !f.make || !f.model}
        data-testid="est-vehicle-save"
        className="mt-3 h-9 px-4 rounded-md bg-[#1d3557] hover:bg-[#152941] text-white text-[12.5px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
      >
        <Check size={12} /> {saving ? "Saving…" : "Save & Use"}
      </button>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
      <div className="relative mt-1">
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full h-9 px-2 pr-7 rounded-md border border-[#E4E7EC] bg-white text-[13px] focus:border-[#457b9d] outline-none appearance-none"
        >
          <option value="">Select…</option>
          {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#a8dadc] pointer-events-none" />
      </div>
    </label>
  );
}
